// SETTINGS V2 — Contact create/edit dialog (SETTINGS_V2_SP3 §8).
//
// Second consumer of SettingsV2DialogShell (620px desktop / fullscreen phone).
// Binds the reused customerContacts slice unchanged (v2 is a view swap). Open-
// state lives in settingsV2UiSlice.openDialog === "contact"; the edit TARGET
// rides customerContacts.contact (legacy parity). File→Avatar rename mechanics
// reproduced VERBATIM (§8.2): read side is contact.file, write side is
// model.avatar; the create path closes BEFORE the refetch, the edit path
// refetches BEFORE it closes. Payload is EXACTLY
// { name, position, department, email, phoneNumber, avatar } — no userId, no
// isDefault, no id (§8.2 parity; the backend UserId-wipe is Q-SETV2-12, NOT ours).
//
// FLAGGED micro-improvement (§8.2): `busy={submitting}` swaps the form for the
// shell Loader and blocks double-submit — the legacy dialog allowed a double
// submit during the awaits. No validator/payload change.
//
// Styling: inline styles + scoped CSS-var tokens + the v2 atoms + shared
// UploadImage (its crop modal is MUI-portalled by MUI itself — sanctioned
// shared component, opens above the shell at MUI zIndex 1300 + later DOM order).
// No MUI/sx/dt/Tailwind authored here. SEC-REQ-2: no import-time side effects.
import React, { useCallback, useEffect, useState } from "react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { emailRegex, phoneRegex } from "../../../../shared/validators";
import UploadImage from "../../../../shared/components/UploadImage";
import { FileModel } from "../../../../shared/models/models";
import { ChangeCustomerContact } from "../../../shared/models/customerContactsModels";
import {
  changeContact,
  createContact,
  getContacts,
  selectContact,
} from "../../customerContactsSlice";
import { closeDialog } from "../settingsV2UiSlice";
import { SInputField } from "../atoms";
import SettingsV2DialogShell from "./SettingsV2DialogShell";

type ContactForm = {
  name: string;
  position: string;
  department: string;
  email: string;
  phoneNumber: string;
};

const EMPTY_FORM: ContactForm = {
  name: "",
  position: "",
  department: "",
  email: "",
  phoneNumber: "",
};

const ContactDialogV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const { isPhone } = useBreakpoint();

  const open = useAppSelector((s) => s.settingsV2Ui.openDialog === "contact");
  const contact = useAppSelector((s) => s.customerContacts.contact);
  const isNew = !contact;

  const [form, setForm] = useState<ContactForm>(EMPTY_FORM);
  const [file, setFile] = useState<FileModel | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (k: keyof ContactForm) => (v: string) =>
    setForm((f) => ({ ...f, [k]: v }));
  const markTouched = (f: string) =>
    setTouched((prev) => ({ ...prev, [f]: true }));

  // Seed on open (§8.2) — replaces legacy's `key={contact-dialog-${open}}`
  // remount-reset trick with an explicit effect (same observable behavior).
  useEffect(() => {
    if (open) {
      setForm(
        contact
          ? {
              name: contact.name,
              position: contact.position,
              department: contact.department,
              email: contact.email,
              phoneNumber: contact.phoneNumber,
            }
          : EMPTY_FORM
      );
      setFile(contact?.file ?? null); // READ side: contact.file
      setTouched({});
    }
  }, [open, contact]);

  const handleClose = useCallback(() => {
    dispatch(closeDialog());
    dispatch(selectContact(null));
    setForm(EMPTY_FORM);
    setFile(null);
    setTouched({});
  }, [dispatch]);

  // Validity (§8.3 — only name required; email/phone checked when non-empty).
  const isValid =
    form.name.trim() !== "" &&
    (form.email === "" ||
      (emailRegex.test(form.email) && form.email.length <= 100)) &&
    (form.phoneNumber === "" ||
      (phoneRegex.test(form.phoneNumber) && form.phoneNumber.length <= 25));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true, phoneNumber: true });
    if (!isValid || submitting) return;

    const model: ChangeCustomerContact = { ...form, avatar: file }; // WRITE side: avatar
    if (isNew) {
      setSubmitting(true);
      await dispatch(createContact(model));
      handleClose(); // legacy: close, THEN refetch
      await dispatch(getContacts());
      setSubmitting(false);
    } else if (contact) {
      setSubmitting(true);
      await dispatch(changeContact({ id: contact.id, model }));
      await dispatch(getContacts()); // legacy: refetch, THEN close
      handleClose();
      setSubmitting(false);
    }
  };

  const nameError =
    touched.name && form.name.trim() === ""
      ? tCommon("forms.required")
      : undefined;
  const emailError =
    touched.email &&
    form.email !== "" &&
    (!emailRegex.test(form.email) || form.email.length > 100)
      ? t("settings:invalidEmail")
      : undefined;
  const phoneError =
    touched.phoneNumber &&
    form.phoneNumber !== "" &&
    (!phoneRegex.test(form.phoneNumber) || form.phoneNumber.length > 25)
      ? t("account:invalidPhone")
      : undefined;

  const footer = (
    <>
      <button type="button" className="btn btn-ghost" onClick={handleClose}>
        {tCommon("buttons.cancel")}
      </button>
      <button
        type="submit"
        form="contactFormV2"
        className="btn btn-primary"
        disabled={submitting}
        style={{ background: "var(--mod-accent)", borderColor: "var(--mod-accent)" }}
      >
        {isNew ? tCommon("create") : tCommon("saveChanges")}
      </button>
    </>
  );

  return (
    <SettingsV2DialogShell
      open={open}
      onClose={handleClose}
      width={620}
      title={isNew ? t("settings:createContact") : t("settings:editContact")}
      labelledById="sv2-contact-dialog-title"
      busy={submitting}
      footer={footer}
    >
      <form
        id="contactFormV2"
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div
          style={{
            display: "flex", gap: 18,
            flexDirection: isPhone ? "column" : "row",
            alignItems: isPhone ? "center" : "flex-start",
          }}
        >
          {/* Avatar column (File→Avatar read seed) */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <UploadImage
              file={file}
              maxFileSizeMb={2}
              onChangeFile={setFile}
              deleteButtonTitle={t("settings:deleteAvatar")}
              cropVariant="circle"
              minCropWidth={120}
              modalWidth="396px"
              aspectRatio={1}
            />
            <div style={{ maxWidth: 200 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-2)", marginBottom: 4 }}>
                {t("settings:avatarRequirements")}
              </div>
              <ul
                style={{
                  margin: 0, paddingLeft: 16, fontSize: 12,
                  color: "var(--muted)", lineHeight: 1.6,
                }}
              >
                <li>{t("settings:avatarSize")}</li>
                <li>{t("settings:avatarFileType")}</li>
                <li>{t("settings:avatarMaxSize")}</li>
              </ul>
            </div>
          </div>

          {/* Fields column — 2-col pairs collapse to 1-col on phone */}
          <div
            style={{
              flex: 1, minWidth: 0, display: "grid",
              gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr", gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }} onBlur={() => markTouched("name")}>
              <SInputField
                label={tCommon("forms.name")}
                required
                value={form.name}
                onChange={setField("name")}
                error={nameError}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <SInputField
                label={t("settings:position")}
                value={form.position}
                onChange={setField("position")}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <SInputField
                label={t("settings:department")}
                value={form.department}
                onChange={setField("department")}
              />
            </div>
            <div style={{ minWidth: 0 }} onBlur={() => markTouched("email")}>
              <SInputField
                label={tCommon("forms.email")}
                mono
                value={form.email}
                onChange={setField("email")}
                error={emailError}
              />
            </div>
            <div style={{ minWidth: 0 }} onBlur={() => markTouched("phoneNumber")}>
              <SInputField
                label={tCommon("forms.phone")}
                type="tel"
                mono
                value={form.phoneNumber}
                onChange={setField("phoneNumber")}
                error={phoneError}
              />
            </div>
          </div>
        </div>
      </form>
    </SettingsV2DialogShell>
  );
};

export default ContactDialogV2;
